/* behaviorTrendCore.ts — 행동 지표 개선 판정·표기의 **코어 정본** (2026-09-27 behaviorTrend.ts에서 분리)
 *
 * 왜 코어로 옮겼나: 서버 자동 복기 알림(lib/ai/reviewAutoNotice.js)이 화면과 **같은 판정·라벨·단위**를
 *   써야 한다. 처음엔 서버가 `redesign/live/behaviorTrend.ts`를 직접 import했는데, Vercel 배포본에는
 *   `mobile/src/domain/core`만 올라가서(.vercelignore) 로컬 빌드는 통과하고 **배포만 실패**했다.
 *   → 서버와 앱이 함께 쓰는 순수 함수는 코어에 둔다. 화면 파일은 여기서 다시 내보낸다.
 * 설계 근거: docs/specs/2026-08-03-review-enrichment-design.md §5 (오독 방지 3종)
 */

// ═══════════════════════════════════════════════════════════
// 지표 정의 버전 (2026-09-28 물타기 비율 전환 · docs/specs/2026-09-28-averaging-down-ratio-design.md §2)
//
// 회차 stats는 DB(sim_reviews.stats.behaviorMetrics)에 **그때 정의로** 영구 저장된다.
// 지표 정의를 바꾸면 옛 회차와 새 회차가 같은 key로 섞인다 — "24건"이 "24%"로 그려지고,
// 알림 판정이 건수와 비율을 비교한다. 그래서:
//   ① 새 회차는 stats.behaviorMetricsVersion에 현재 버전을 남긴다(없으면 v1).
//   ② 버전이 다른 두 회차를 비교·나열할 때는 **그 사이에 정의가 바뀐 지표만** 측정 불가(null)로 둔다.
// 다음에 다른 지표 정의를 바꿀 때도 버전을 올리고 METRIC_CHANGED_AT에 한 줄 추가하면 된다.
// ═══════════════════════════════════════════════════════════

/** 현재 행동 지표 정의 버전. 정의를 바꿀 때만 올린다. */
export const BEHAVIOR_METRICS_VERSION = 2;

/** 지표별 "이 버전에서 정의가 바뀌었다". v2 = 물타기 누적 건수 → 최근 추가매수 20건 중 비율(%). */
export const METRIC_CHANGED_AT: Readonly<Record<string, number>> = Object.freeze({
  averaging_down: 2,
});

/** stats에서 지표 버전을 읽는다. 필드가 없으면 버전 표식 도입 전(v1). */
export function metricsVersionOf(stats: { behaviorMetricsVersion?: unknown } | null | undefined): number {
  const v = stats?.behaviorMetricsVersion;
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 ? v : 1;
}

/**
 * fromVersion으로 저장된 지표 배열을 toVersion 기준으로 **비교 가능한 값만** 남긴다.
 * 두 버전 사이에 정의가 바뀐 지표는 value=null·exceeded=false·sample 제거(= 측정 불가 취급).
 * 원본 배열은 건드리지 않는다.
 */
export function comparableMetrics<T extends { key: string; value: number | null; exceeded?: boolean; sample?: unknown }>(
  metrics: T[] | null | undefined, fromVersion: number, toVersion: number,
): T[] {
  const list = Array.isArray(metrics) ? metrics : [];
  if (fromVersion === toVersion) return list.slice();
  const lo = Math.min(fromVersion, toVersion);
  const hi = Math.max(fromVersion, toVersion);
  return list.map((m) => {
    const changed = METRIC_CHANGED_AT[m?.key];
    if (changed == null || changed <= lo || changed > hi) return m;
    const { sample: _sample, ...rest } = m;
    void _sample;
    return { ...rest, value: null, exceeded: false } as T;
  });
}

/** 개선 판정. flat = 변화 없음, unknown = 두 시점 중 하나라도 측정 불가. */
export type Delta = 'improved' | 'worsened' | 'flat' | 'unknown';

/**
 * 첫 측정 → 최근 측정의 개선 판정.
 * ⚠ 선의 방향으로 좋고 나쁨을 말하면 안 된다 — direction이 좋은 방향을 준다(§5 오독 방지 ②).
 *   churn(평균 보유일)은 **올라가야** 개선이고, 나머지 8종은 내려가야 개선이다.
 */
export function judgeDelta(
  first: number | null, last: number | null, direction: 'lower' | 'higher',
): Delta {
  if (first == null || last == null) return 'unknown';
  if (first === last) return 'flat';
  const better = direction === 'higher' ? last > first : last < first;
  return better ? 'improved' : 'worsened';
}

/** 값 표기 — 지표별 단위. churn만 '일', 비율 지표는 '%', 나머지는 '건'.
 *  ⚠ averaging_down은 v2(2026-09-28)부터 비율이다. v1 회차 값(건수)은 comparableMetrics가 null로 내려
 *    여기까지 오지 않는다 — 옛 값을 '%'로 찍는 일이 없게 하는 것이 그 함수의 역할이다. */
export function fmtValue(key: string, value: number | null): string {
  if (value == null) return '—';
  if (key === 'churn') return `${Math.round(value * 10) / 10}일`;
  if (key === 'fomo_chase' || key === 'falling_knife' || key === 'breakeven_exit'
    || key === 'no_thesis' || key === 'concentration' || key === 'averaging_down') return `${Math.round(value)}%`;
  if (key === 'no_focus') return `${Math.round(value)}종목`;
  return `${Math.round(value)}건`;
}

/**
 * 추이 표·알림용 **짧은 축 이름**.
 *
 * 🔴 2026-08-04 e2e 육안에서 나온 문제: 서버 라벨은 판결문("회전문 트레이더")이라 375px 폭에서
 *   ellipsis로 잘려 지표가 무엇인지 못 읽혔다 — 라벨을 못 읽으면 이 화면은 의미가 없다.
 * 그리고 추이 표에서는 판결보다 **측정 대상**이 정확하다: 값이 "4.2일"이면 그 축의 이름은
 *   '회전문 트레이더'가 아니라 '평균 보유일'이다(설계 §5도 지표를 "평균 보유일·물타기 횟수"로 부른다).
 * 미등록 key는 서버 라벨을 그대로 쓴다(지표가 추가돼도 빈칸이 되지 않는다).
 */
const SHORT_LABELS: Record<string, string> = {
  churn: '보유 기간',      // '평균 보유일'은 375px 폭에서 잘렸다 — 값(2.4일)이 단위를 이미 말해준다
  overtrade: '과매매',
  concentration: '몰빵 비중',
  averaging_down: '물타기',
  fomo_chase: '추격 매수',
  falling_knife: '하락 매수',
  no_focus: '종목 수',     // churn('보유 기간')과 앞글자가 겹치지 않게
  breakeven_exit: '본전 매도',
  no_thesis: '근거 없음',
};

export function shortLabel(key: string, fallback: string): string {
  return SHORT_LABELS[key] || fallback;
}
